import React from 'react'

// components
import { Avatar } from '@/components/commons/images/Avatar.component'
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'
import { useSocialConnection } from '@/shared/hooks/socialConnection'
import { cssClass } from '@/shared/utils'
import { metaButtonsContainer } from 'containers/analytics/styles/metaComponents.css'
import {
  useOauthDisconnect,
  useOauthStatus,
} from 'containers/oAuth/hooks/useSocialsConnection'
import {
  OauthRequestSocialType,
  OauthStatus,
} from 'containers/oAuth/types/oAuth'
import { ArrowRightIcon } from 'public/assets/v2/images/icons'
import { avatarContainer, secondaryImage } from '../../styles/index.css'
import { GoogleModalConnection, SocialModalDisconnect } from './GoogleModal'

interface ButtonConnectionProps {
  social: OauthRequestSocialType
  allowDisconnect?: boolean
}
interface ImageMapper {
  [key: string]: {
    main: string
    secondary?: string
    modal?: string
  }
}

const imageMapper: ImageMapper = {
  youtube: {
    main: 'google',
    secondary: 'youtube',
    modal: 'google_no_border',
  },
  instagram: {
    main: 'meta',
    secondary: 'instagram',
  },
  tiktok: {
    main: 'tiktok',
  },
  // 'instagram-basic': {
  //   main: 'instagram',
  // },
}

interface RenderButtonProps {
  handleClick: (social?: OauthRequestSocialType) => void
  social: OauthRequestSocialType
  loading?: boolean
  label?: string
  className?: string
  avatarSize?: string | number
  isInModal?: boolean
}

export const RenderButton: React.FC<RenderButtonProps> = ({
  handleClick,
  social,
  label,
  className,
  isInModal,
}) => {
  const {
    isLoading: loading,
    // data: status
  } = useOauthStatus()
  const mappedImage = imageMapper[social]

  // const isFullMetaConnected =
  //   status?.[OauthRequestSocialType.INSTAGRAM] === OauthStatus.CONNECTED

  const mainImage = isInModal
    ? mappedImage.modal || mappedImage.main
    : imageMapper[social].main

  const secondaryImageUrl = !isInModal
    ? imageMapper[social].secondary
    : undefined

  const avatarSize = imageMapper[social].secondary ? 36 : 48

  // const metaAvatar = imageMapper.main ? 24 : 36

  // const instaAvatar = imageMapper.secondary ? 24 : 36

  const isMetaButtons = social === OauthRequestSocialType.INSTAGRAM

  // const containerClass = isMetaButtons
  //   ? 'p-16 border-solid border-secondary flex flex-column gap-12 border-1 rounded-16'
  //   : ''
  const containerClass = ''

  return (
    <div className={containerClass}>
      <div
        onClick={() => handleClick()}
        className={cssClass([
          'full-w p-16 flex items-center gap-12 rounded-16',
          'justify-space-between',
          // isMetaButtons
          //   ? ''
          //   :
          'bg-white-primary border-solid border-secondary border-1 cursor-pointer',
          className,
        ])}
      >
        <div className={cssClass([avatarContainer, 'relative'])}>
          <Avatar
            image={IMAGE_PATHS_V2.analytics + mainImage + '_avatar.png'}
            alt={mainImage}
            size={avatarSize}
            key={imageMapper[social].main}
            imageFit="contain"
          />
          {secondaryImageUrl && (
            <div className={secondaryImage}>
              <Avatar
                image={
                  IMAGE_PATHS_V2.analytics +
                  imageMapper[social].secondary +
                  '_avatar.png'
                }
                key={imageMapper[social].secondary}
                alt={social}
                size={'100%'}
                customBorderRadius={8}
              />
            </div>
          )}
        </div>
        {loading && !isMetaButtons ? (
          <LoaderSpinner size={24} />
        ) : (
          <p className="m-0 text-14 leading-20 text-18--tablet leading-24--tablet font-medium text-primary">
            {label}
          </p>
        )}

        <ArrowRightIcon
          // className={isMetaButtons ? 'text-white' : 'text-primary'}
          className="text-primary"
        />
      </div>

      {isMetaButtons && (
        <div className={cssClass(['flex gap-12', metaButtonsContainer])}>
          {/* {!isFullMetaConnected && (
            <ConnectMetaButton
              handleClick={() =>
                handleClick(OauthRequestSocialType.INSTAGRAM_BASIC)
              }
              social={OauthRequestSocialType.INSTAGRAM_BASIC}
              avatarSize={instaAvatar}
              loading={loading}
            />
          )} */}

          {/* <ConnectMetaButton
            handleClick={() => handleClick(OauthRequestSocialType.INSTAGRAM)}
            social={OauthRequestSocialType.INSTAGRAM}
            avatarSize={metaAvatar}
            loading={loading}
          /> */}
        </div>
      )}
    </div>
  )
}

export const ButtonConnection: React.FC<ButtonConnectionProps> = ({
  social,
  allowDisconnect,
}) => {
  const { openSocialLinkAuthentication } = useSocialConnection()
  const { data: status, isLoading: loading } = useOauthStatus()
  const { mutateAsync, isLoading: disconnecting } = useOauthDisconnect()
  const [connectionModalOpen, setConnectionModalOpen] =
    React.useState<boolean>(false)
  const [disconnectModalOpen, setDisconnectModalOpen] =
    React.useState<boolean>(false)

  const socialStatus = status?.[social]
  const isConnected = socialStatus === OauthStatus.CONNECTED
  const useModal = social === 'youtube'

  const socialName =
    social === 'youtube'
      ? 'Google / Youtube'
      : social === 'instagram'
      ? 'Meta / Instagram'
      : social.charAt(0).toUpperCase() + social.slice(1)

  const connectedLabel = allowDisconnect
    ? `Disconnect ${socialName}`
    : `${socialName} connected`

  const label = isConnected
    ? `${connectedLabel}`
    : // : social === 'instagram'
      // ? 'Connect Instagram with Meta'
      `Sign in with ${socialName}`
  const handleClick = (optionalSocial?: OauthRequestSocialType) => {
    if (loading) return true

    if (isConnected) {
      setDisconnectModalOpen(true)
    } else {
      if (useModal && !connectionModalOpen) {
        setConnectionModalOpen(true)
      } else {
        openSocialLinkAuthentication(optionalSocial || social)
      }
    }
  }

  const handleDisconnect = async () => {
    await mutateAsync(social)
    setDisconnectModalOpen(false)
  }

  const avatarSize = imageMapper[social].secondary ? 36 : 48

  return (
    <>
      <GoogleModalConnection
        modalOpen={connectionModalOpen}
        onClose={() => setConnectionModalOpen(false)}
        handleClick={handleClick}
        social={social}
        loading={loading || disconnecting}
      />

      <SocialModalDisconnect
        modalOpen={disconnectModalOpen}
        onClose={() => setDisconnectModalOpen(false)}
        handleClick={handleDisconnect}
        social={social}
        loading={loading || disconnecting}
      />

      <RenderButton
        avatarSize={avatarSize}
        handleClick={handleClick}
        social={social}
        loading={loading || disconnecting}
        label={label}
      />
    </>
  )
}
